import React, { useState } from 'react';
import { X, Search, Package, Truck, MapPin, CheckCircle, Clock } from 'lucide-react';

// Demo delivery data
const demoDeliveries = {
  'SHIP2024001': {
    id: 'SHIP2024001',
    orderId: 'SILK2024001',
    piece: 'Noor-e-Chandni Gharara',
    recipient: 'Meera S.',
    destination: 'Mumbai, Maharashtra',
    status: 'in_transit',
    estimatedDelivery: '18th January 2025',
    courier: 'BlueDart Premium',
    timeline: [
      { stage: 'Dispatched', date: '12th January 2025', time: '10:30 AM', completed: true, icon: 'package' },
      { stage: 'In Transit', date: '14th January 2025', time: '2:15 PM', completed: false, current: true, icon: 'truck', location: 'Delhi Hub' },
      { stage: 'Out for Delivery', date: 'Pending', completed: false, icon: 'mappin' },
      { stage: 'Delivered', date: 'Pending', completed: false, icon: 'check' }
    ]
  },
  'SHIP2024002': {
    id: 'SHIP2024002',
    orderId: 'SILK2024002',
    piece: 'Gulbadan Lehenga',
    recipient: 'Ananya R.',
    destination: 'Bangalore, Karnataka',
    status: 'out_for_delivery',
    estimatedDelivery: '20th December 2024',
    courier: 'DTDC Express',
    timeline: [
      { stage: 'Dispatched', date: '15th December 2024', time: '9:00 AM', completed: true, icon: 'package' },
      { stage: 'In Transit', date: '17th December 2024', time: '11:45 AM', completed: true, icon: 'truck', location: 'Hyderabad Hub' },
      { stage: 'Out for Delivery', date: '20th December 2024', time: '8:30 AM', completed: false, current: true, icon: 'mappin', location: 'Koramangala, Bangalore' },
      { stage: 'Delivered', date: 'Pending', completed: false, icon: 'check' }
    ]
  },
  'SHIP2024003': {
    id: 'SHIP2024003',
    orderId: 'SILK2024003',
    piece: 'Begum Shahi Anarkali',
    recipient: 'Priya K.',
    destination: 'Delhi, NCR',
    status: 'delivered',
    estimatedDelivery: '10th December 2024',
    courier: 'BlueDart Premium',
    deliveredDate: '10th December 2024',
    timeline: [
      { stage: 'Dispatched', date: '5th December 2024', time: '11:00 AM', completed: true, icon: 'package' },
      { stage: 'In Transit', date: '7th December 2024', time: '3:30 PM', completed: true, icon: 'truck', location: 'Lucknow Hub' },
      { stage: 'Out for Delivery', date: '10th December 2024', time: '9:15 AM', completed: true, icon: 'mappin', location: 'Greater Kailash, Delhi' },
      { stage: 'Delivered', date: '10th December 2024', time: '2:45 PM', completed: true, icon: 'check' }
    ]
  }
};

const DeliveryTracking = ({ isOpen, onClose }) => {
  const [trackingId, setTrackingId] = useState('');
  const [delivery, setDelivery] = useState(null);
  const [error, setError] = useState('');
  const [showDemo, setShowDemo] = useState(false);

  const getIcon = (iconName) => {
    const iconMap = {
      package: Package,
      truck: Truck,
      mappin: MapPin,
      check: CheckCircle
    };
    return iconMap[iconName] || Package;
  };

  const getStatusText = (status) => {
    const statusMap = {
      dispatched: 'Dispatched',
      in_transit: 'In Transit',
      out_for_delivery: 'Out for Delivery',
      delivered: 'Delivered'
    };
    return statusMap[status] || status;
  };

  const getStatusClass = (status) => {
    if (status === 'delivered') return 'delivered';
    if (status === 'out_for_delivery') return 'arriving';
    return 'transit';
  };

  const handleSearch = () => {
    const trimmedId = trackingId.trim().toUpperCase();
    if (demoDeliveries[trimmedId]) {
      setDelivery(demoDeliveries[trimmedId]);
      setError('');
    } else {
      setDelivery(null);
      setError('Tracking number not found. Please check your tracking ID or contact us on WhatsApp.');
    }
  };

  const handleDemoDelivery = (demoId) => {
    setDelivery(demoDeliveries[demoId]);
    setTrackingId(demoId);
    setError('');
    setShowDemo(false);
  };

  const resetAndClose = () => {
    setTrackingId('');
    setDelivery(null);
    setError('');
    setShowDemo(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="delivery-modal-overlay" onClick={resetAndClose} data-testid="delivery-tracking-modal">
      <div className="delivery-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="delivery-modal-close" onClick={resetAndClose} data-testid="delivery-close-btn">
          <X size={24} />
        </button>

        <div className="delivery-modal-header">
          <h2 className="heading-2">Heritage Delivery</h2>
          <p className="body-regular delivery-modal-subtitle">
            Track your precious creation's journey to you
          </p>
        </div>

        {!delivery ? (
          <div className="delivery-search-container">
            <div className="delivery-search-box">
              <input
                type="text"
                placeholder="Enter Tracking Number"
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="delivery-search-input"
                data-testid="tracking-id-input"
              />
              <button className="btn-primary delivery-search-btn" onClick={handleSearch} data-testid="delivery-search-btn">
                <Search size={18} />
                Track
              </button>
            </div>
            
            {error && <p className="delivery-error">{error}</p>}

            <div className="delivery-demo-section">
              <p className="body-small delivery-demo-text">
                Don't have a tracking number? View demo shipments to see our delivery experience.
              </p>
              <button 
                className="btn-secondary delivery-demo-btn" 
                onClick={() => setShowDemo(!showDemo)}
                data-testid="view-demo-deliveries-btn"
              >
                {showDemo ? 'Hide Demo' : 'View Demo Shipments'}
              </button>
              
              {showDemo && (
                <div className="delivery-demo-list">
                  {Object.values(demoDeliveries).map((demo) => (
                    <div 
                      key={demo.id} 
                      className="delivery-demo-item"
                      onClick={() => handleDemoDelivery(demo.id)}
                      data-testid={`demo-delivery-${demo.id}`}
                    >
                      <div className="delivery-demo-info">
                        <span className="delivery-demo-id">{demo.id}</span>
                        <span className="delivery-demo-piece">{demo.piece}</span>
                      </div>
                      <span className={`delivery-demo-status ${getStatusClass(demo.status)}`}>
                        {getStatusText(demo.status)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="delivery-details">
            <div className="delivery-info-card">
              <div className="delivery-info-header">
                <div>
                  <h3 className="heading-3">{delivery.piece}</h3>
                  <p className="body-small delivery-tracking-id">Tracking: {delivery.id}</p>
                </div>
                <div className={`delivery-status-badge ${getStatusClass(delivery.status)}`}>
                  {delivery.status === 'delivered' ? (
                    <CheckCircle size={18} />
                  ) : (
                    <Clock size={18} />
                  )}
                  <span>{getStatusText(delivery.status)}</span>
                </div>
              </div>
              
              <div className="delivery-info-details">
                <div className="delivery-info-row">
                  <span className="delivery-info-label">Recipient:</span>
                  <span className="delivery-info-value">{delivery.recipient}</span>
                </div>
                <div className="delivery-info-row">
                  <span className="delivery-info-label">Destination:</span>
                  <span className="delivery-info-value">{delivery.destination}</span>
                </div>
                <div className="delivery-info-row">
                  <span className="delivery-info-label">Courier:</span>
                  <span className="delivery-info-value">{delivery.courier}</span>
                </div>
                <div className="delivery-info-row">
                  <span className="delivery-info-label">
                    {delivery.status === 'delivered' ? 'Delivered On:' : 'Est. Delivery:'}
                  </span>
                  <span className="delivery-info-value delivery-date">
                    {delivery.status === 'delivered' ? delivery.deliveredDate : delivery.estimatedDelivery}
                  </span>
                </div>
              </div>
            </div>

            <div className="delivery-timeline">
              <h4 className="heading-4 delivery-timeline-title">Shipping Status</h4>
              {delivery.timeline.map((item, index) => {
                const IconComponent = getIcon(item.icon);
                return (
                  <div 
                    key={index} 
                    className={`delivery-timeline-item ${item.completed ? 'completed' : ''} ${item.current ? 'current' : ''}`}
                  >
                    <div className="delivery-timeline-icon">
                      <IconComponent size={18} />
                    </div>
                    <div className="delivery-timeline-content">
                      <div className="delivery-timeline-main">
                        <span className="delivery-timeline-stage">{item.stage}</span>
                        {item.location && (
                          <span className="delivery-timeline-location">{item.location}</span>
                        )}
                      </div>
                      <div className="delivery-timeline-time">
                        <span className="delivery-timeline-date">{item.date}</span>
                        {item.time && <span className="delivery-timeline-hour">{item.time}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button 
              className="btn-secondary delivery-back-btn" 
              onClick={() => setDelivery(null)}
              data-testid="delivery-back-btn"
            >
              Track Another Shipment
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryTracking;
